/**
 * Use Collaboration Hook
 * 
 * React hook to integrate sync service with Monaco editor
 * Manages cursor synchronization, file updates, and collaborator presence
 */

import { useEffect, useRef, useCallback } from 'react';
import syncService from '../services/syncService';
import MonacoCollaborationManager from '../utils/monacoCollaborationManager';

const useCollaboration = (editor, monaco, file, projectId, serviceName) => {
  const collaborationManagerRef = useRef(null);
  const cursorUpdateTimeoutRef = useRef(null);
  const isApplyingRemoteUpdateRef = useRef(false);

  // Initialize collaboration manager
  useEffect(() => {
    if (!editor || !monaco || !file) {
      return;
    }

    // Create collaboration manager
    collaborationManagerRef.current = new MonacoCollaborationManager(editor, monaco);

    console.log('[useCollaboration] Collaboration manager initialized');

    // Cleanup
    return () => {
      if (collaborationManagerRef.current) {
        collaborationManagerRef.current.dispose();
        collaborationManagerRef.current = null;
      }
    };
  }, [editor, monaco, file?.path || file?.name]); // Use file path/name instead of entire object

  // Track cursor position changes
  useEffect(() => {
    if (!editor || !file || !projectId || !serviceName) {
      return;
    }

    const filePath = file.path || file.name;

    const handleCursorChange = () => {
      const position = editor.getPosition();
      if (!position) return;

      // Debounce cursor updates
      if (cursorUpdateTimeoutRef.current) {
        clearTimeout(cursorUpdateTimeoutRef.current);
      }

      cursorUpdateTimeoutRef.current = setTimeout(() => {
        syncService.updateCursor(filePath, serviceName, {
          line: position.lineNumber,
          column: position.column,
        });
      }, 150); // 150ms throttle
    };

    const disposable = editor.onDidChangeCursorPosition(handleCursorChange);

    return () => {
      disposable.dispose();
      if (cursorUpdateTimeoutRef.current) {
        clearTimeout(cursorUpdateTimeoutRef.current);
      }
    };
  }, [editor, file?.path || file?.name, projectId, serviceName]); // Use file path instead of object

  // Listen for remote cursor updates
  useEffect(() => {
    console.log('[useCollaboration] Setting up cursor listener:', { 
      hasFile: !!file, 
      hasProjectId: !!projectId, 
      hasServiceName: !!serviceName, 
      hasManager: !!collaborationManagerRef.current 
    });
    
    if (!file || !projectId || !serviceName || !collaborationManagerRef.current) {
      console.log('[useCollaboration] Skipping cursor listener setup - missing requirements');
      return;
    }

    const currentFilePath = file.path || file.name;
    
    console.log('[useCollaboration] Subscribing to cursorMoved events for:', currentFilePath);

    const handleRemoteCursor = (data) => {
      const { userId, username, position, filePath, serviceName: remoteService, color } = data;
      
      console.log('[useCollaboration] Received cursor update:', { userId, username, position, filePath, remoteService, currentFilePath, currentService: serviceName });
      
      // Only show cursors for the current file
      if (filePath !== currentFilePath || remoteService !== serviceName) {
        console.log('[useCollaboration] Cursor update ignored - different file/service');
        return;
      }

      console.log('[useCollaboration] Updating cursor decoration');
      // Check manager still exists before updating
      if (collaborationManagerRef.current) {
        collaborationManagerRef.current.updateCursor(
          userId,
          username,
          color,
          position
        );
      }
    };

    const unsubscribe = syncService.subscribe('cursorMoved', handleRemoteCursor);
    console.log('[useCollaboration] Cursor listener subscribed');

    return () => {
      console.log('[useCollaboration] Unsubscribing cursor listener');
      unsubscribe();
    };
  }, [file?.path || file?.name, projectId, serviceName, collaborationManagerRef.current]); // Include manager in dependencies

  // Listen for users leaving (remove their cursors)
  useEffect(() => {
    if (!collaborationManagerRef.current) {
      return;
    }

    const handleUserLeft = (data) => {
      const { userId } = data;
      collaborationManagerRef.current.removeCursor(userId);
    };

    const unsubscribe = syncService.subscribe('userLeft', handleUserLeft);

    return () => {
      unsubscribe();
    };
  }, []);

  // Listen for file updates from other users
  useEffect(() => {
    if (!editor || !file) {
      return;
    }

    console.log('[useCollaboration] Setting up file update listener...');

    const handleFileUpdate = (data) => {
      const { filePath, serviceName: updateServiceName, content, userId, connectionId } = data;
      
      console.log('[useCollaboration] File update received:', { 
        filePath, 
        serviceName: updateServiceName, 
        currentFile: file.path, 
        currentService: serviceName,
        fromUserId: userId,
        myUserId: syncService.userId,
        fromConnectionId: connectionId,
        myConnectionId: syncService.connectionId
      });
      
      // Ignore updates from our own connection/tab (but allow same user in different tabs)
      if (connectionId && connectionId === syncService.connectionId) {
        console.log('[useCollaboration] Ignoring update from same connection/tab');
        return;
      }
      
      // Only apply updates for the current file
      if (filePath === file.path && updateServiceName === serviceName) {
        console.log('[useCollaboration] Applying remote file update to editor', { contentLength: content?.length });
        
        if (!content) {
          console.warn('[useCollaboration] No content in file update!');
          return;
        }
        
        // Set flag to prevent onChange from sending update back
        isApplyingRemoteUpdateRef.current = true;
        
        // Save current cursor position
        const position = editor.getPosition();
        
        // Update editor content
        try {
          editor.setValue(content);
          
          // Restore cursor position
          if (position) {
            editor.setPosition(position);
          }
          
          console.log('[useCollaboration] Editor updated with remote changes');
        } finally {
          // Clear flag after the next animation frame to ensure onChange has processed
          requestAnimationFrame(() => {
            isApplyingRemoteUpdateRef.current = false;
          });
        }
      }
    };

    const unsubscribe = syncService.subscribe('fileUpdated', handleFileUpdate);

    return () => {
      unsubscribe();
    };
  }, [editor, file?.path, serviceName]);

  // Clean up stale cursors periodically
  useEffect(() => {
    if (!collaborationManagerRef.current) {
      return;
    }

    const interval = setInterval(() => {
      collaborationManagerRef.current.cleanupStaleCursors(30000); // 30 seconds
    }, 10000); // Check every 10 seconds

    return () => {
      clearInterval(interval);
    };
  }, []);

  // Get current collaborators
  const getCollaborators = useCallback(() => {
    if (!collaborationManagerRef.current) {
      return [];
    }
    return collaborationManagerRef.current.getActiveCursors();
  }, []);

  return {
    collaborationManager: collaborationManagerRef.current,
    getCollaborators,
    isApplyingRemoteUpdate: () => isApplyingRemoteUpdateRef.current,
  };
};

export default useCollaboration;
